import React from "react";
import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { useNotification } from "@refinedev/core";
import _ from "lodash";
import { Trash2 } from "lucide-react";

import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";

export function PromoCodesField({ id, inputProps }: AutoFormFieldProps) {
  const form = useFormContext();
  const value: string[] = useWatch({ name: id }) ?? [];
  const { open } = useNotification();

  // Derive the parent offer path (e.g. "offers.0") from the field id
  const parentPath = id.split(".").slice(0, -1).join(".");
  const offerType = useWatch({ name: `${parentPath}.type` });

  if (offerType !== "discount") return null;

  const setValue = (newValues: string[]) => {
    form.setValue(id, newValues, { shouldDirty: true });
    inputProps.onChange({
      target: { name: id, value: newValues },
    } as unknown as React.ChangeEvent<HTMLInputElement>);
  };

  return (
    <div className="flex flex-col space-y-2 text-foreground w-full">
      <div className="text-sm font-medium">Promo Codes</div>
      <div className="text-xs text-muted-foreground">
        Paste line-separated codes or generate using [prefix]:[start]...[end]{" "}
        and press <kbd>Enter</kbd>
      </div>
      <Textarea
        placeholder="Enter value"
        onKeyDown={(e) => {
          if (e.key !== "Enter") return;
          e.preventDefault();
          const codes = e.currentTarget.value.split("\n");
          let formattedCodes: string[] = [];

          if (codes.length <= 0) {
            open?.({
              message: "Failed to generate codes",
              description: "Fill code input based on the instructions provided",
              type: "error",
            });
            return;
          }

          if (codes.length >= 0 && codes[0].includes("...")) {
            const [prefix, boundsStr] = codes[0].split(":");
            const [start, end] = boundsStr.split("...");
            for (
              let i = Number.parseInt(start);
              i <= Number.parseInt(end);
              i++
            ) {
              formattedCodes.push(`${prefix}${i}`);
            }
          } else if (codes[0] === "clear") {
            setValue([]);
            return;
          } else {
            formattedCodes = codes.filter((c) => !_.isEmpty(c));
          }

          setValue([...formattedCodes, ...value]);
        }}
      />
      {value.length > 0 && (
        <div className="max-h-52 overflow-auto">
          {value.map((code, idx) => (
            <div key={idx} className="flex flex-row space-x-2">
              <div>
                {idx + 1}. {code}
              </div>
              <Trash2
                className="w-4 h-4 hover:cursor-pointer"
                onClick={() => setValue(value.filter((_, i) => i !== idx))}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
